'use client';

import { faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormDivider } from '@form/index';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import SelectableCard from '@ui/SelectableCard';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatAmount, formatDate } from '~/components/Banking/shared/format';
import BankDetails from './BankDetails';
import type { SelectedTransaction } from './types';

const BankTransactionLinker = dynamic(
    () => import('~/components/Banking/BankTransactionLinker/BankTransactionLinker'),
    { ssr: false }
);

interface Props {
    clubSlug: string;
    hasBankAccounts: boolean;
    selected: SelectedTransaction | null;
    onSelect: (transaction: SelectedTransaction | null) => void;
}

/**
 * "Zahlung zuordnen": the standing context beside the fields, in the shape BookingRail set.
 *
 * Must be rendered inside the receipt's Form — it scores transactions against what the user is
 * typing right now, via useWatch (plain watch() does not re-render custom inputs under the
 * shared Form). The pick is held, not written: a transaction cannot be linked to an invoice that
 * does not exist yet, so the assistant links it right after the store call.
 */
const BelegRail = ({ clubSlug, hasBankAccounts, selected, onSelect }: Props) => {
    const { t } = useTranslation('invoice');

    const live = useWatch({ name: ['value', 'debtor', 'iban', 'invoice_number', 'document_date', 'remarks'] }) as [
        number | string | null,
        string | null,
        string | null,
        string | null,
        string | null,
        string | null
    ];

    // Debounced so a keystroke does not refetch: these feed the linker's SWR key.
    const [value, debtor, iban, invoiceNumber, documentDate, remarks] = useDebouncedValue(live, 500, true) ?? [];

    // The field is euro, the linker expects cents.
    const invoiceValue = useMemo(() => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && '' !== value && null !== value ? Math.round(parsed * 100) : null;
    }, [value]);

    if (!hasBankAccounts) {
        return (
            <FormDivider title={t('belegAssistant.payment.title')}>
                <BankDetails />
                <span className="tw-text-sm tw-text-accents-6">{t('belegAssistant.payment.noBankAccount')}</span>
            </FormDivider>
        );
    }

    return (
        <FormDivider title={t('belegAssistant.payment.title')} className="tw-flex tw-flex-col tw-gap-3">
            <BankDetails />

            {selected ? (
                <SelectableCard flat selected onSelect={() => onSelect(null)}>
                    <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-1">
                        <span className="tw-text-sm tw-font-semibold">
                            {formatAmount(selected.amount, undefined, true)}
                        </span>
                        <span className="tw-text-xs tw-text-accents-6">
                            {formatDate(selected.booking_date)}
                            {selected.counterparty ? ` · ${selected.counterparty}` : ''}
                        </span>
                        <span className="tw-text-xs tw-text-success">{t('belegAssistant.payment.willBeLinked')}</span>
                    </div>
                </SelectableCard>
            ) : (
                <span className="tw-text-xs tw-text-accents-6">
                    <FontAwesomeIcon icon={faBan} className="tw-mr-1" />
                    {t('belegAssistant.payment.noneHint')}
                </span>
            )}

            <BankTransactionLinker
                clubSlug={clubSlug}
                mode="deferred"
                selectedTransaction={selected}
                onSelect={onSelect}
                paymentBasePath="invoice-external"
                invoiceValue={invoiceValue}
                invoiceDebtor={debtor ?? null}
                invoiceIban={iban ?? null}
                invoiceNumber={invoiceNumber ?? null}
                invoiceDate={documentDate ?? null}
                invoiceRemarks={remarks ?? null}
            />
        </FormDivider>
    );
};

export default BelegRail;
